import type { Question } from "@shared/schema";

// The display letter for an answer option at a given index (0 -> "A",
// 1 -> "B", …). Supports variable-length option lists past the original 4.
export function answerLabel(index: number): string {
  return String.fromCharCode(65 + index);
}

// True when the stem (trimmed) ends with a sentence-closing punctuation
// mark — used to decide whether the answer choices should be capitalized
// as sentence fragments rather than left as the AI emitted them.
const stemEndsWithSentencePunctuation = (stem: string): boolean => {
  const trimmed = (stem || "").trim();
  if (!trimmed) return false;
  const last = trimmed[trimmed.length - 1];
  return last === "." || last === "?" || last === "!";
};

// Heuristic: does this answer look like math / a number rather than a
// natural-language fragment? Math answers should NOT have their first
// letter capitalized — variable case (`x` vs `X`) carries meaning, and
// "120 miles" reads worse as "120 Miles". Three signals say "leave it":
//   1. Starts with a digit or arithmetic sign (12, −5, +3, 3x, $3.50, 120 miles)
//   2. Contains an equation/inequality (=, ≤, ≥, ≠, <, >)
//   3. Has an arithmetic operator wedged between non-space chars (3x+1, a/b, 2^n)
// Anything else is treated as natural language.
const looksLikeMathOrNumber = (s: string): boolean => {
  const trimmed = (s || "").trim();
  if (!trimmed) return false;
  if (/^[-−+$]?\d/.test(trimmed)) return true;
  if (/[=≤≥≠<>]/.test(trimmed)) return true;
  if (/\S[+\-×÷*/^]\S/.test(trimmed)) return true;
  return false;
};

// Capitalize the first letter character in a string, preserving any
// leading whitespace, punctuation, or numbers. ("  three" -> "  Three",
// "42" -> "42", "the answer" -> "The answer"). No-ops on math-shaped
// answers so we don't mangle equations or coefficients.
const capitalizeFirstLetter = (s: string): string => {
  if (!s) return s;
  if (looksLikeMathOrNumber(s)) return s;
  let i = 0;
  while (i < s.length && !/[a-z]/i.test(s[i])) i++;
  if (i >= s.length) return s;
  return s.slice(0, i) + s[i].toUpperCase() + s.slice(i + 1);
};

// When the stem reads like a full sentence (ends in . ? !), answer
// choices should be capitalized as sentence fragments. Returns a new
// Question with every answer's first letter capitalized; no-ops when
// the stem doesn't end with sentence punctuation so we don't mangle
// answers for stems intended as fill-in-the-blank fragments.
// capitalizeFirstLetter additionally skips math-shaped answers — see
// looksLikeMathOrNumber.
export function normalizeAnswerCapitalization(question: Question): Question {
  if (!stemEndsWithSentencePunctuation(question.stem || "")) return question;
  return {
    ...question,
    answers: question.answers.map(capitalizeFirstLetter),
  };
}

// Detect "All of the above" / "None of the above" — these reference the
// other options, so they have to stay at the bottom of the list regardless
// of alphabet. Sorting them up to the top would break the question.
const isReferenceAnswer = (s: string): boolean =>
  /^\s*(all|none|both|neither)\s+of\s+the\s+(above|preceding|following)\b/i.test(s);

const firstToken = (s: string): string => s.trim().split(/\s+/)[0] || "";
const isNumeric = (t: string): boolean => /^-?\d+(\.\d+)?$/.test(t);

// Sort an MCQ's answer options by their first token:
//  - "All/None of the above" stay pinned to the end (in their original
//    relative order).
//  - Numeric first-tokens sort numerically (smaller first).
//  - Otherwise, alphabetical case-insensitive on the first token.
//  - Numbers come before words when mixed.
// Returns a new Question with the answers in the new order and
// `correctAnswer` re-mapped to the new index.
export function sortQuestionAnswers(question: Question): Question {
  if (!Array.isArray(question.answers) || question.answers.length <= 1) {
    return question;
  }

  const indexed = question.answers.map((answer, originalIdx) => ({ answer, originalIdx }));
  const regular = indexed.filter((x) => !isReferenceAnswer(x.answer));
  const pinned = indexed.filter((x) => isReferenceAnswer(x.answer));

  regular.sort((a, b) => {
    const ta = firstToken(a.answer);
    const tb = firstToken(b.answer);
    const aNum = isNumeric(ta);
    const bNum = isNumeric(tb);
    if (aNum && bNum) return parseFloat(ta) - parseFloat(tb);
    if (aNum && !bNum) return -1; // numbers ahead of words
    if (!aNum && bNum) return 1;
    return ta.toLowerCase().localeCompare(tb.toLowerCase());
  });

  const newOrder = [...regular, ...pinned];
  const newAnswers = newOrder.map((x) => x.answer);
  const newCorrect = newOrder.findIndex((x) => x.originalIdx === question.correctAnswer);

  // Build pre-sort letter → post-sort letter map. Only entries where the
  // letter actually changed are stored; identity mappings are omitted so
  // the object stays empty (and therefore absent) when the list was already
  // in sorted order.
  const answerLetterMap: Record<string, string> = {};
  newOrder.forEach((item, newIdx) => {
    const preLetter = String.fromCharCode(65 + item.originalIdx);
    const postLetter = String.fromCharCode(65 + newIdx);
    if (preLetter !== postLetter) {
      answerLetterMap[preLetter] = postLetter;
    }
  });

  // NOTE on double-sort staleness: originalIdx is relative to the INPUT
  // answers array, not the original BCE emission order. If this question
  // was already sorted (e.g. after a distractor regen), this map reflects
  // only the permutation from the re-sort of the already-sorted array.
  // A map is only valid for a score BCE computed on the pre-sort order,
  // which is exactly the session scores from /api/socrates-score. The
  // isolated /api/questions/score endpoint instead computes on the answers
  // array verbatim (the displayed order), so every store of one strips
  // answerLetterMap first (scoreAddedItem, the card rescore handler) and a
  // stale map is never consumed against a fresh score.

  return {
    ...question,
    answers: newAnswers,
    correctAnswer: newCorrect >= 0 ? newCorrect : question.correctAnswer,
    // Explicitly clear any incoming map before conditionally re-attaching
    // a fresh one: an empty new map must not preserve a stale map from a
    // prior sort through an identity permutation.
    answerLetterMap: undefined,
    ...(Object.keys(answerLetterMap).length > 0 ? { answerLetterMap } : {}),
  };
}

// Rewrite embedded option-letter references in a SOCRATES feedback string
// using the pre-sort → post-sort letter map stored on the question.
// Handles these formats emitted by BCE (either casing of "option(s)"):
//   "(A)"               → "(C)"
//   "Option A"          → "Option C"
//   "option C"          → "option A"
//   "Options A and B"   → "Options C and A"
//   "options C and A"   → "options B and D"
// Only CAPITAL letters count as references: a lowercase letter after
// "option" is an article ("give another option a comparable structure"),
// never an option reference, and stays exactly as written. Real option
// letters in findings are always capitals.
// No-ops when letterMap is absent or empty (question was never sorted, or
// sort produced no reordering).
// IMPORTANT: Apply this function exactly once per feedback string against
// the source text (pre-sort BCE emission). It is NOT idempotent — a map
// containing transitive entries (A→B, B→C) would corrupt text on a second
// pass. In practice QuestionCard re-derives feedback from source each
// render, which guarantees single application.
export function remapFeedbackLetters(
  text: string,
  letterMap: Record<string, string> | undefined,
): string {
  if (!letterMap || Object.keys(letterMap).length === 0) return text;

  // Replace "Options X and Y" first (two-letter form) to avoid the
  // single-letter patterns consuming the first letter before we see the pair.
  let result = text.replace(
    /\b([Oo]ptions?)\s+([A-Z])\s+and\s+([A-Z])\b/g,
    (_, word: string, l1: string, l2: string) =>
      `${word} ${letterMap[l1] ?? l1} and ${letterMap[l2] ?? l2}`,
  );

  // Replace parenthesised form: (A)
  result = result.replace(
    /\(([A-Z])\)/g,
    (_, l: string) => `(${letterMap[l] ?? l})`,
  );

  // Replace "Option X" / "option X" (single capital letter, word boundary on
  // both sides). The capital requirement keeps "option a" (article) untouched.
  result = result.replace(
    /\b([Oo]ption)\s+([A-Z])\b/g,
    (_, word: string, l: string) => `${word} ${letterMap[l] ?? l}`,
  );

  return result;
}
